import React from 'react';
import { Instagram, Youtube, Facebook } from 'lucide-react';

/**
 * SocialIconLinks
 * --------------------------------------------------------------------------
 * Compact, icon-only social media link row.
 *
 * Props:
 *   - coach: object that may contain `instagram_url`, `tiktok_url`,
 *            `youtube_url`, `facebook_url`. Each is optional; the icon is
 *            only rendered when the URL is a non-empty string.
 *   - size: 'sm' | 'md' (default 'md') — controls icon + button size.
 *   - testIdPrefix: prefix for `data-testid` attributes (default 'social').
 *   - className: extra wrapper classes (e.g. for margin/alignment overrides).
 *
 * Design notes:
 *   - We never render a button for a platform the coach didn't fill in —
 *     this keeps the row tight and avoids broken/empty links.
 *   - Lucide ships Instagram/Youtube/Facebook but not TikTok, so we inline
 *     a minimal monochrome SVG that matches the visual weight of the others.
 *   - All links open in a new tab with rel="noopener noreferrer" for safety.
 */

// --- Custom TikTok glyph (currentColor so it inherits text color) -----
function TikTokIcon({ className = 'h-5 w-5' }) {
    return (
        <svg
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className={className}
            aria-hidden="true"
        >
            <path
                d="M16.5 3a5.5 5.5 0 0 0 5 5v3a8.5 8.5 0 0 1-5-1.6V15a6 6 0 1 1-6-6c.36 0 .7.03 1.03.1v3.1A3 3 0 1 0 13.5 15V3h3Z"
                fill="currentColor"
            />
        </svg>
    );
}

const PLATFORMS = [
    { key: 'instagram_url', label: 'Instagram', Icon: Instagram, testId: 'instagram' },
    { key: 'tiktok_url', label: 'TikTok', Icon: TikTokIcon, testId: 'tiktok' },
    { key: 'youtube_url', label: 'YouTube', Icon: Youtube, testId: 'youtube' },
    { key: 'facebook_url', label: 'Facebook', Icon: Facebook, testId: 'facebook' },
];

export function SocialIconLinks({
    coach,
    size = 'md',
    testIdPrefix = 'social',
    className = '',
}) {
    if (!coach) return null;
    const present = PLATFORMS.filter((p) => (coach[p.key] || '').trim().length > 0);
    if (present.length === 0) return null;

    // Size tokens — keep them constrained so the row stays visually tight.
    const sizeClasses = size === 'sm'
        ? 'h-8 w-8'
        : 'h-10 w-10';
    const iconClasses = size === 'sm' ? 'h-4 w-4' : 'h-5 w-5';

    return (
        <div
            className={`flex flex-wrap items-center gap-2 ${className}`}
            data-testid={`${testIdPrefix}-icons-row`}
        >
            {present.map(({ key, label, Icon, testId }) => (
                <a
                    key={key}
                    href={coach[key]}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`Open ${label}`}
                    title={label}
                    data-testid={`${testIdPrefix}-${testId}-link`}
                    className={`inline-flex ${sizeClasses} items-center justify-center rounded-full border border-[#0a1628]/15 bg-white text-[#0a1628] transition-shadow duration-200 hover:shadow-[0_6px_14px_rgba(10,22,40,0.10)] hover:border-[#00d4ff]/40 hover:text-[#0084a8] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#00d4ff] focus-visible:ring-offset-2`}
                >
                    <Icon className={iconClasses} />
                </a>
            ))}
        </div>
    );
}

export default SocialIconLinks;
